import React, { Component, createRef } from 'react';
import { findDOMNode } from 'react-dom';
import ResizeObserver from 'rc-resize-observer';
import classnames from 'classnames';
import { getGlobalStorage } from '@platform/api';
import { getScrollBarSize, calcVariableVirtualInfo, formatTree } from './utils';

import TableHeader from './components/TableHeader';
import TableBody from './components/TableBody';
import TableFooter from './components/TableFooter';

import './index.less';

const scrollBarSize = getScrollBarSize();

const formatColumn = function (tree, callback) {
    return formatTree(tree, callback, {
        idKey: 'key',
        isLeafKey: 'isLeaf',
    });
};

class TableNext extends Component {
    static defaultProps = { 
        rowHeight: 30,
        maxBodyHeight: 300, // number | 'auto'
        bufferSize: 1,
        columns: [],
        rows: [],
        autoRowHeight: false,
        scrollContainer: () => window,
    }

    tableWrapperRef = createRef();
    tableHeaderWrapperRef = createRef();
    tableBodyWrapperRef = createRef();
    tableFooterWrapperRef = createRef();
    horizontalScrollBarRef = createRef();
    verticalScrollBarRef = createRef();

    horizontalScrollElements = [
        this.tableHeaderWrapperRef, 
        this.tableBodyWrapperRef, 
        this.tableFooterWrapperRef, 
        this.horizontalScrollBarRef, 
    ];
    verticalScrollElements = [
        this.tableBodyWrapperRef,
        this.verticalScrollBarRef,
    ];
    virtualInfo = {};

    readyToScroll = new Set();
    scrollDirection = '';

    constructor (props) {
        super(props);
        this.state = {
            bodyWidth: 0,
            bodyHeight:0,
            scrollLeft: 0, 
        };
    }

    render () {
        let { scrollLeft, bodyWidth, fixedTop, fixedBottom } = this.state;
        let { bufferSize, className, autoRowHeight, flex, virtualConfig = {} } = this.props;

        let columnInfo = this.calcColumnInfo();

        // 横向虚拟滚动信息
        let horizontalVirtualInfo = calcVariableVirtualInfo({
            offset: scrollLeft,
            visibleSize: bodyWidth - columnInfo.left.width - columnInfo.right.width,
            bufferSize, 
            itemsSize: columnInfo.middle.columnsWidth,
        });

        if (virtualConfig.x === false) {
            horizontalVirtualInfo = {
                ...horizontalVirtualInfo,
                bottomBlank: 0,
                bottomBlankCount: 0,
                endRenderIndex: columnInfo.middle.leafColumns.length - 1,
                endViewIndex: columnInfo.middle.leafColumns.length - 1,
                startRenderIndex: 0,
                startViewIndex: 0,
                topBlank: 0,
                topBlankCount: 0,
            };
        }

        this.tableBodyScrollWidth = columnInfo.left.width + columnInfo.middle.width + columnInfo.right.width;
        // this.hasVerticalScrollBar = bodyHeight < this.tableBodyWrapperRef.current?.scrollHeight;
        this.hasHorizontalScrollBar = bodyWidth < this.tableBodyScrollWidth + (this.hasVerticalScrollBar ? scrollBarSize : 0);

        let renderInfo = {
            bodyWidth,
            tableBodyWidth: bodyWidth,
            horizontalVirtualInfo,
            columnInfo,
            onVirtualInfo: this.handleVirtualInfo,
            hasVerticalScrollBar: this.hasVerticalScrollBar,
            hasHorizontalScrollBar: this.hasHorizontalScrollBar,
            scrollBarSize,
            isFixLeft: scrollLeft > 0,
            isFixRight: scrollLeft < columnInfo.left.width + columnInfo.middle.width + columnInfo.right.width - bodyWidth,
            fixedTop, 
            fixedBottom,
        };

        return <div 
            ref={this.tableWrapperRef}
            className={classnames(
                "base-table-next", 
                className, 
                { 
                    'flex-table': flex,
                    'flex-container': flex,
                    'auto-row-height': autoRowHeight,
                },
            )} 
        >
            {this.renderTableHeader(renderInfo)}
            {this.renderTableBody(renderInfo)}
            {this.renderTableFooter(renderInfo)}
            {this.renderHorizontalScrollBar(renderInfo)}
        </div>;
    }

    componentDidMount() {
        this.bindEvent();
        this.handleWindowScroll();
    }

    componentDidUpdate() {
        this.handleWindowScroll();
    }

    componentWillUnmount() {
        this.removeEvent();
    }

    handleVirtualInfo = virtualInfo => {
        this.virtualInfo = virtualInfo;
    }

    renderTableHeader = renderInfo => {
        return <TableHeader {...this.props} {...renderInfo} ref={this.tableHeaderWrapperRef}></TableHeader>;
    }

    /**
     * 两种布局模式
     */
    renderTableBody = renderInfo => {
        let { flex, bodyStyle = {}, _onScrollBarStatusChange } = this.props,
            { bodyHeight } = this.state;
        /**
         * 1、容器高度决定内部高度
         *  容器flex
         *  纵向滚动条高度与容器高度统一
         * 2、内部高度决定容器高度
         *  内部设最大高度
         *  纵向滚动条高度和TableBody高度保持统一
         *  可伸缩
         */
        return <ResizeObserver
            onResize={({ height }, target) => {
                this.setState({
                    bodyWidth: target.clientWidth,
                    bodyHeight: height,
                });
            }}
        >
            <TableBody 
                {...this.props} 
                {...renderInfo}
                tableRef={this.tableBodyWrapperRef}
                verticalScrollBarRef={this.verticalScrollBarRef}
                bodyStyle={{
                    ...bodyStyle,
                    height: flex ? bodyHeight : (bodyStyle.height ?? 'auto'),
                    maxHeight: flex ? 'none' : bodyStyle.maxHeight,
                }}
                onScrollBarStatusChange={status => {
                    this.hasVerticalScrollBar = status;
                    _onScrollBarStatusChange?.(status);
                    this.forceUpdate();
                }}
            ></TableBody>
        </ResizeObserver>;
    }

    renderTableFooter = renderInfo => {
        return <TableFooter {...this.props} {...renderInfo} ref={this.tableFooterWrapperRef}></TableFooter>;
    }

    // 横向滚动条
    renderHorizontalScrollBar = () => {
        let { bodyWidth, fixedBottom, fixedDistance } = this.state,
            verticalScrollBarSize = this.hasVerticalScrollBar ? scrollBarSize : 0,
            horizontalScrollBarSize = this.hasHorizontalScrollBar ? scrollBarSize : 0;

        return <>
            <div 
                className={classnames(
                    "horizontal-scroll-bar-wrapper",
                    { "fix-bottom": fixedBottom },
                )}
                style={{ 
                    display: 'flex', 
                    width: bodyWidth,
                    bottom: fixedDistance,
                }}
            >
                <div 
                    ref={this.horizontalScrollBarRef}
                    style={{ width: bodyWidth - verticalScrollBarSize, height: horizontalScrollBarSize }}
                    className="horizontal-scroll-bar" 
                >
                    <div className="horizontal-scroll-item" style={{ width: this.tableBodyScrollWidth, height: horizontalScrollBarSize, minHeight: 1 }}></div>
                </div>
                {this.hasVerticalScrollBar && <div className="scroll-bar-blank" style={{ height: horizontalScrollBarSize, width: verticalScrollBarSize }}></div>}
            </div>
            {fixedBottom && <div
                style={{
                    width: bodyWidth,
                    height: horizontalScrollBarSize,
                }}
            ></div>}
        </>;
    }

    calcColumnInfo = () => {
        // TODO:可以缓存
        let leftColumns = [], middleColumns = [], rightColumns = [];
        let leftWidth = 0, middleWidth = 0, rightWidth = 0; // 总宽度
        let leftWidths = [], middleWidths = [], rightWidths = []; // 每一列的宽度
        let middleLeafColumns = [];
        let useTotal = false;
        let { columns, columnsWidth, addBlankCol = true } = this.props,
            { bodyWidth } = this.state;

        let ncc_automated = getGlobalStorage('localStorage', '_ncc_automated_');

        columnsWidth = { ...columnsWidth };
        
        let { height } = formatColumn(columns, column => {
            column = { ...column };
            let { isLeaf = true, fixed, depth = 0, width = 0, key, visible = true, istotal } = column;
            
            if (isNaN(width)) {
                width = 0;
            }

            if (ncc_automated == '1') {
                // 自动化测试开关打开
                fixed = false;
            }

            if (isLeaf) {
                if (istotal) {
                    useTotal = true;
                }

                if (!visible) {
                    width = 0;
                } else if (columnsWidth[key]) {
                    width = columnsWidth[key];
                } else {
                    columnsWidth[key] = width;
                }
                column = { ...column, width };
            }

            // 多表头不支持固定列
            switch (isLeaf && depth === 0 && fixed) {
                case 'left':
                case true:
                    leftColumns.push(column);
                    leftWidth += column.width;
                    leftWidths.push(column.width);
                    break;
                case 'right':
                    rightColumns.push(column);
                    rightWidth += column.width;
                    rightWidths.push(column.width);
                    break;
                default:
                    middleColumns.push(column);
                    if (isLeaf) {
                        middleLeafColumns.push(column);
                        middleWidth += column.width;
                        middleWidths.push(column.width);
                    }
                    break;
            }

            return column;
        });

        // 添加空白的占位列，用于优化显示
        let blankWidth = bodyWidth - leftWidth - middleWidth - rightWidth - (this.hasVerticalScrollBar ? scrollBarSize : 0);
        if (addBlankCol && blankWidth > 0) {
            let blankCol = {
                title: null,
                key: 'BLANK_COL',
                width: blankWidth,
                isLeaf: true,
                depth: 0,
                render: () => null,
            };

            let lastMiddleColumn = middleColumns[middleColumns.length - 1],
                lastMiddleLeafColumn = middleLeafColumns[middleLeafColumns.length - 1];

            if (lastMiddleLeafColumn) {
                blankCol.leafIndex = lastMiddleLeafColumn.leafIndex + 1;
            } else {
                blankCol.leafIndex = 0;
            }

            if (lastMiddleColumn) {
                blankCol.dataIndex = lastMiddleLeafColumn.dataIndex + 1;
            } else {
                blankCol.dataIndex = 1;
            }

            rightColumns.forEach(col => {
                col.leafIndex++;
                col.dataIndex++;
                col.range = col.range.map(e => e + 1);
                col.viewIndex++;
            });

            middleColumns.push(blankCol);
            middleLeafColumns.push(blankCol);
            middleWidths.push(blankCol.width);
            middleWidth += blankCol.width;
            columnsWidth[blankCol.key] = blankCol.width;
        }

        let leafColumns = [].concat(leftColumns, middleLeafColumns, rightColumns),
            leafWidth = [].concat(leftWidths, middleWidths, rightWidths),
            flatColumns = [].concat(leftColumns, middleColumns, rightColumns);

        // TODO:上下一共遍历了两次，看是否优化
        return { 
            headerRowsCount: height,
            flatColumns, // TODO:header算法
            left: {
                columns: leftColumns,
                leafColumns: leftColumns,
                ...this.calcWidthInfo(leftColumns),
            },
            middle: {
                columns: middleColumns,
                columnsWidth: middleWidths,
                leafColumns: middleLeafColumns,
                ...this.calcWidthInfo(middleColumns),
            },
            right: {
                columns: rightColumns,
                leafColumns: rightColumns,
                ...this.calcWidthInfo(rightColumns, { reverse: true }),
            },
            leaf: { 
                columns: leafColumns,
                columnsWidth: leafWidth,
                columnsWidthMap: columnsWidth,
            },
            useTotal,
        };
    }

    // 计算总宽度以及偏移量
    calcWidthInfo = (columns, { reverse = false } = {}) => {
        return columns[reverse ? 'reduceRight' : 'reduce'](({ offsets, width }, { width: columnWidth, isLeaf }) => {
            if (isLeaf) {
                offsets[reverse ? 'unshift' : 'push'](width);
                width += columnWidth;
            }
            return { offsets, width };
        }, { offsets: [], width: 0 });
    }

    bindEvent = () => {
        // 接管默认的滚动事件
        // 解决纵向滚动有些许白屏的问题
        // 解决横向滚动有些许错位的问题
        this.horizontalScrollElements.forEach(element => {
            element.current.addEventListener('wheel', this.syncHorizontalWheel);
            element.current.addEventListener('scroll', this.handleHorizontalScroll);
        });
        this.verticalScrollElements.forEach(element => {
            element.current.addEventListener('wheel', this.syncVerticalWheel);
        });

        let { scrollContainer } = this.props;

        scrollContainer?.()?.addEventListener('scroll', this.handleWindowScroll);
        window.addEventListener('resize', this.handleWindowScroll);
    }

    removeEvent = () => {
        this.horizontalScrollElements.forEach(element => {
            element.current.removeEventListener('wheel', this.syncHorizontalWheel);
            element.current.removeEventListener('scroll', this.handleHorizontalScroll);
        });
        this.verticalScrollElements.forEach(element => {
            element.current.removeEventListener('wheel', this.syncVerticalWheel);
        });

        let { scrollContainer } = this.props;

        scrollContainer?.()?.removeEventListener('scroll', this.handleWindowScroll);
        window.removeEventListener('resize', this.handleWindowScroll);
    }

    // TODO:后面优化一下可读性
    handleWindowScroll = () => {
        let { fixHeader, fixFooter, scrollContainer: getScrollContainer } = this.props;

        let { fixedTop, fixedBottom } = this.state;

        let scrollContainer = getScrollContainer?.(), 
            containerBottom = this.getDOMBottom(scrollContainer);
        
        fixFooter && [
            this.horizontalScrollBarRef,
        ].map(e=>{
            let { bottom } = e.current.getBoundingClientRect();
            if (
                containerBottom - this.tableBodyWrapperRef.current.getBoundingClientRect().bottom >= 
                this.horizontalScrollBarRef.current.offsetHeight + this.tableFooterWrapperRef.current.offsetHeight || 
                this.tableHeaderWrapperRef.current.getBoundingClientRect().bottom > containerBottom
            ) {
                if (fixedBottom) {
                    this.setState({ 
                        fixedBottom: false,
                        fixedDistance: 'auto',
                    });
                }
            } else if (bottom > containerBottom) {
                if (!fixedBottom) {

                    this.setState({ 
                        fixedBottom: true,
                        fixedDistance: this.getDOMBottom(this.getFixedContainer()) - containerBottom,
                    });
                }
            }
        });

        fixHeader && [
            this.tableHeaderWrapperRef, 
        ].map(e=>{
            let { top } = e.current.getBoundingClientRect();
            if (
                this.tableBodyWrapperRef.current.getBoundingClientRect().top > 
                this.tableHeaderWrapperRef.current.offsetHeight || 
                this.horizontalScrollBarRef.current.getBoundingClientRect().bottom < 0
            ) {
                if (fixedTop) {
                    this.setState({ fixedTop: false });
                }
            } else if (top < 0) {
                if (!fixedTop) {
                    this.setState({ fixedTop: true });
                }
            } 
        });
    }

    getDOMBottom = DOMNode => {
        let fixedContainerBottom;

        if (DOMNode === window || DOMNode === document || DOMNode === document.body || !DOMNode) {
            fixedContainerBottom = document.body.clientHeight;
        } else {
            fixedContainerBottom = DOMNode.getBoundingClientRect().bottom;
        }

        return fixedContainerBottom;
    }

    getFixedContainer = () => {
        let TableDOM = findDOMNode(this),
            parentNode = TableDOM.parentElement;
        while (parentNode !== document.body) {
            let { transform, perspective, filter } = getComputedStyle(parentNode);

            // 这三个属性会影响position: fixed的定位基准
            if (transform !== 'none' || perspective !== 'none' || filter !== 'none') {
                break;
            }

            parentNode = parentNode.parentElement;
        }

        return parentNode;
    }

    // 横向滚动事件
    handleHorizontalScroll = event => {
        let { currentTarget } = event,
            currentScrollLeft = currentTarget.scrollLeft;

        // 横向滚动
        if (this.readyToScroll.has(currentTarget)) {
            this.readyToScroll.delete(currentTarget);
            return;
        } else {
            this.readyToScroll = new Set(this.horizontalScrollElements.map(e => e.current));
            this.readyToScroll.delete(currentTarget);
        }

        // 不通过wheel触发滚动
        // 比如：代码控制、focus等
        if (currentScrollLeft !== this.state.scrollLeft) {
            this.scrollX(currentScrollLeft, currentTarget);
            this.setState({ scrollLeft: currentScrollLeft });
        }
    }

    // 同步横向滚动（滚轮事件）
    syncHorizontalWheel = event => {
        let { currentTarget, deltaX, deltaY } = event;
        
        if (Math.abs(deltaX) >= Math.abs(deltaY) || event.shiftKey) {
            let { scrollLeft: currentScrollLeft, clientWidth, scrollWidth } = currentTarget,
                scrollLeft = currentScrollLeft + (Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY);

            if (scrollLeft > 0 && scrollLeft < scrollWidth - clientWidth) {
                // 禁用滚动
                // 解决overflow: hidden导致的外层抖动（触发了mac触摸板的前进后退）
            }
            event.preventDefault();

            currentTarget.scrollLeft = scrollLeft;
        }
    }

    // 同步纵向滚动（滚轮事件）
    syncVerticalWheel = event => {
        let { currentTarget, deltaX, deltaY } = event;
        if (event.shiftKey && currentTarget === this.tableBodyWrapperRef.current) return; // tableBody区域+shift控制横向滚动

        if (Math.abs(deltaX) < Math.abs(deltaY)) {
            let { scrollTop: currentScrollTop, clientHeight, scrollHeight } = currentTarget,
                scrollTop = currentScrollTop + deltaY;

            if (scrollTop > 0 && scrollTop < scrollHeight - clientHeight) {
                // 禁用滚动
                // 解决overflow: hidden导致的外层抖动（触发了mac触摸板的前进后退）
            }
            this.hasVerticalScrollBar && event.preventDefault();
            this.scrollY(scrollTop);

        } 
    }

    scrollX = (scrollLeft, current) => {
        this.horizontalScrollElements.forEach(element => {
            if (current !== element.current) {
                element.current.scrollLeft = scrollLeft;
            }
        });
    }

    scrollY = scrollTop => {
        this.verticalScrollElements.forEach(element => element.current.scrollTop = scrollTop);
    }
}

export default TableNext;